import {inject, Injectable} from '@angular/core';
import {ProjectFile} from '../../api';
import {Observable, of, shareReplay, tap} from 'rxjs';
import {StateConfigService} from '../state/state-config.service';
import {ProjectFileTableService} from '../files/project-file-table.service';

/**
 * Service to prefetch project files in the background.
 * Used by check components to start loading files while the user is still selecting rules.
 *
 * Geladen wird dieselbe schlanke Projektion wie in der Dateienansicht (`GET .../files/table`) statt der
 * vollen Entity: die kostet pro Datei eine presigned S3-URL, den eager gelesenen Analyse-Lob und
 * Lazy-Nachladen beim Serialisieren -- fuer eine Dateiliste, aus der die Checks nur Typ, Status,
 * Klassifizierung und Ordnerhierarchie brauchen. Details in {@link ProjectFileTableService}.
 */
@Injectable({providedIn: 'root'})
export class FilePrefetchService {
  private fileTableService = inject(ProjectFileTableService);
  private stateConfigService = inject(StateConfigService);

  private cachedProjectId: number | null = null;
  private cache$: Observable<ProjectFile[]> | null = null;
  private cachedFiles: ProjectFile[] | null = null;

  /**
   * Start prefetching files for the current project.
   * Safe to call multiple times – will only trigger one API call.
   */
  prefetch(): void {
    const projectId = this.stateConfigService.getProjectId();
    if (this.cachedProjectId === projectId && this.cache$) {
      return; // Already prefetching or cached for this project
    }
    this.cachedProjectId = projectId;
    this.cachedFiles = null;
    this.cache$ = this.fileTableService.getTableRows(projectId).pipe(
      tap(files => this.cachedFiles = files),
      shareReplay(1)
    );
    // Subscribe to trigger the request
    this.cache$.subscribe();
  }

  /**
   * Get the prefetched files. Returns cached data immediately if available,
   * otherwise returns the in-flight observable.
   */
  getFiles(): Observable<ProjectFile[]> {
    if (this.cachedFiles) {
      return of(this.cachedFiles);
    }
    if (this.cache$) {
      return this.cache$;
    }
    // Not prefetched yet – fetch now
    this.prefetch();
    return this.cache$!;
  }

  /**
   * Check if files are already loaded.
   */
  get isLoaded(): boolean {
    return this.cachedFiles !== null;
  }

  /**
   * Clear the cache (e.g. when navigating away from checks).
   */
  clear(): void {
    this.cache$ = null;
    this.cachedFiles = null;
    this.cachedProjectId = null;
  }
}

